import React from 'react';
import HojaCaratula from './base/HojaCaratula';

// Etiquetas EXACTAS de las plantillas (templates/caratula/caratula_pension_*.docx): el modal
// es una vista previa, así que lo que se lee acá tiene que coincidir renglón por renglón con
// el papel. `guardable` marca lo que además vuelve a la base (ver
// CaratulaPensionDocumentoService): el bloque del causante va a tramite_pension (solo si el
// trámite es del módulo propio; un GENERICO viejo no tiene dónde guardarlo), y claves y
// domicilio del titular van al cliente.
//
// CAMPOS es la UNIÓN de las tres variantes; cada layout de abajo nombra solo los suyos. Cuando
// el mismo dato tiene otra etiqueta en otro papel, la entrada extra redirige con `campo`.
const CAMPOS = {
    numeroBeneficio: { label: 'N° Beneficio', tipo: 'texto', guardable: true },
    numeroBeneficioDerivada: { label: 'Numero de Beneficio', tipo: 'texto', campo: 'numeroBeneficio', guardable: true },
    ingreso: { label: 'INGRESO', tipo: 'fecha' },
    pacto: { label: 'PACTO', tipo: 'sino' },

    claveFiscal: { label: 'CLAVE FISCAL', tipo: 'clave', guardable: true },
    claveCvss: { label: 'CLAVE CVSS', tipo: 'clave', guardable: true },
    referencia: { label: 'REFERENCIA', tipo: 'texto' },

    apellidoNombre: { label: 'APELLIDO/NOMBRE', tipo: 'texto' },
    dniCuit: { label: 'DNI/CUIT', tipo: 'texto' },
    telefono: {
        label: 'Nº TELEFONICO', tipo: 'texto',
        nota: 'Trae el teléfono propio y los contactos adicionales del cliente. Se editan desde la ficha del cliente.',
    },

    apellidoNombreCausante: {
        label: 'APELLIDO/NOMBRE DEL CAUSANTE', tipo: 'texto', guardable: true,
        nota: 'El causante no es el cliente del sistema: se guarda en el trámite de Pensión.',
    },
    dniCuilCausante: { label: 'DNI/CUIL', tipo: 'texto', guardable: true },
    fechaNacimientoCausante: { label: 'FECHA DE NACIMIENTO', tipo: 'fecha', guardable: true, noFutura: true },
    fechaDefuncionCausante: { label: 'FECHA DE DEFUNCIÓN', tipo: 'fecha', guardable: true, noFutura: true },

    domicilioTitular: {
        label: 'DOMICILIO DE ELLA', tipo: 'domicilio', estructurado: 'domicilioDniDatos', guardable: true,
        nota: 'Se guarda en el domicilio DNI del cliente (el barrio que ya tenga se conserva). Vacío no borra nada.',
    },
    domicilioTitularDerivada: {
        label: 'DOMICILIO DEL TITULAR', tipo: 'domicilio', estructurado: 'domicilioDniDatos',
        campo: 'domicilioTitular', guardable: true,
        nota: 'Se guarda en el domicilio DNI del cliente (el barrio que ya tenga se conserva). Vacío no borra nada.',
    },
    domicilioCausante: { label: 'DOMICILIO DEL CAUSANTE', tipo: 'texto', guardable: true, placeholder: 'Calle Nro – Ciudad' },
    domicilio: { label: 'DOMICILIO', tipo: 'texto', campo: 'domicilioCausante', guardable: true, placeholder: 'Calle Nro – Ciudad' },

    relacionDependencia: { label: 'RELACION DE DEPENDENCIA', tipo: 'texto', guardable: true },
    autonomoMonotributo: { label: 'AUTONOMO O MONOTRIBUTO', tipo: 'texto', guardable: true },
    cuentaAfip: { label: 'CUENTA AFIP', tipo: 'texto', guardable: true },
    observaciones: { label: 'OBSERVACIONES', tipo: 'multilinea', guardable: true },
};

// Tres carátulas para un trámite de Pensión: la ficha preselecciona la de su modalidad y el
// operador puede elegir otra desde el menú del botón, igual que Jubilación. No son la misma
// carátula con otro título — Directa separa el domicilio del titular y del causante; Derivada
// también, pero suma Numero de Beneficio y no tiene autónomo/monotributo ni cuenta AFIP; Hijo
// con discapacidad suma N° Beneficio y tiene un solo renglón de domicilio (el del causante,
// con etiqueta "DOMICILIO" a secas).
const LAYOUTS = {
    DIRECTA: {
        titulo: 'DATOS PERSONALES',
        leyenda: 'PENSIÓN DIRECTA',
        cabecera: [['ingreso', 'pacto']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['apellidoNombreCausante'],
            ['dniCuilCausante'],
            ['fechaNacimientoCausante'],
            ['fechaDefuncionCausante'],
            ['domicilioTitular'],
            ['domicilioCausante'],
            ['telefono'],
            ['relacionDependencia'],
            ['autonomoMonotributo'],
            ['cuentaAfip'],
            ['observaciones'],
        ],
    },
    DERIVADA: {
        titulo: 'DATOS PERSONALES',
        leyenda: 'PENSIÓN DERIVADA',
        cabecera: [['numeroBeneficioDerivada', 'ingreso', 'pacto']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['apellidoNombreCausante'],
            ['dniCuilCausante'],
            ['fechaNacimientoCausante'],
            ['fechaDefuncionCausante'],
            ['domicilioTitularDerivada'],
            ['domicilioCausante'],
            ['telefono'],
            ['relacionDependencia'],
            ['observaciones'],
        ],
    },
    HIJO_DISCAPACIDAD: {
        titulo: 'DATOS PERSONALES',
        leyenda: 'PENSIÓN POR HIJO CON DISCAPACIDAD',
        cabecera: [['numeroBeneficio', 'ingreso', 'pacto']],
        cuerpo: [
            ['apellidoNombre'],
            ['dniCuit'],
            ['apellidoNombreCausante'],
            ['dniCuilCausante'],
            ['fechaNacimientoCausante'],
            ['fechaDefuncionCausante'],
            ['domicilio'],
            ['telefono'],
            ['relacionDependencia'],
            ['autonomoMonotributo'],
            ['cuentaAfip'],
            ['observaciones'],
        ],
    },
};

// CLAVE FISCAL/CVSS y REFERENCIA van igual en las tres variantes y se dibujan aparte, arriba
// del recuadro, tal como están en el papel.
const FILAS_PREVIAS = [['claveFiscal', 'claveCvss'], ['referencia']];

// La variante la elige el operador en el menú del botón y viaja en `datos.variante`; ante una
// desconocida se cae a DIRECTA.
const hojaDe = (form) => {
    const layout = LAYOUTS[form?.variante] || LAYOUTS.DIRECTA;
    return {
        tituloDoc: layout.titulo,
        leyendaDoc: layout.leyenda,
        cabecera: layout.cabecera,
        filasPrevias: FILAS_PREVIAS,
        cuerpo: layout.cuerpo,
    };
};

/**
 * Modal de la Carátula de Pensión (trámite de Pensión, o un GENERICO viejo). Dibuja el layout
 * de la variante que corresponda y muestra los renglones prellenados desde el cliente y el
 * trámite. Al confirmar, emite el .docx con el DTO tal cual quedó en el formulario (WYSIWYG);
 * lo marcado `guardable` además vuelve a la base.
 */
export default function ModalCaratulaPension(props) {
    return (
        <HojaCaratula
            {...props}
            titulo="Generar Carátula de Pensión"
            nombreDocumento="la Carátula de Pensión"
            campos={CAMPOS}
            layout={hojaDe}
        />
    );
}
